// C7 reset page: opened from the reset email. Checks the one-time 1-hour link, then the person sets a new password.
// Setting it signs them out on every other device.
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, Lock } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useT } from '../i18n';
import { acceptReset, checkReset, type InviteCheck } from '../services';
import { PoweredBy, SurakshaMark } from '../components/common/Brand';
import { LangToggle } from '../components/common/LangToggle';

const inputCls =
  'h-12 w-full rounded-xl border border-sc-line bg-white px-4 text-base text-slate-900 placeholder:text-slate-400 focus:border-sc-teal focus:outline-none focus:ring-2 focus:ring-sc-teal/30';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('t') ?? '';
  const t = useT();
  const navigate = useNavigate();
  const { signIn } = useApp();
  const [info, setInfo] = useState<InviteCheck | null>(null);
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    checkReset(token).then((r) => live && setInfo(r)).catch(() => live && setInfo({ state: 'invalid' }));
    return () => { live = false; };
  }, [token]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (pw.length < 10 || !/\d/.test(pw) || !/[A-Za-zऀ-ॿ]/.test(pw)) return setError(t('welcome.err.weak'));
    if (pw !== pw2) return setError(t('welcome.err.mismatch'));
    setError('');
    setBusy(true);
    try {
      const { email } = await acceptReset(token, pw);
      await signIn(email, pw);
      navigate('/');
    } catch (err) {
      const code = err instanceof Error ? err.message : '';
      if (['USED', 'EXPIRED', 'INVALID'].includes(code)) setInfo({ state: code.toLowerCase() as InviteCheck['state'] });
      else setError(t(code === 'TOO_SHORT' || code === 'NEEDS_LETTER_AND_NUMBER' ? 'welcome.err.weak' : 'welcome.err.failed'));
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-sc-bg px-5 py-6 sm:px-10">
      <div className="flex justify-end"><LangToggle /></div>
      <div className="mx-auto flex w-full max-w-sm flex-1 flex-col justify-center py-10" data-testid="reset-page">
        <SurakshaMark className="h-16 w-16" />
        <p className="mt-3 text-sm font-semibold text-sc-teal" data-testid="welcome-tagline">{t('welcome.tagline')}</p>
        {!info && <p className="mt-6 flex items-center gap-2 text-slate-600" data-testid="welcome-checking"><Loader2 className="h-5 w-5 animate-spin" />{t('welcome.checking')}</p>}

        {info && info.state !== 'ok' && (
          <div className="mt-6" data-testid={`welcome-${info.state}`}>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">{t(`reset.bad.${info.state}.title`)}</h1>
            <p className="mt-2 text-slate-600">{t(`reset.bad.${info.state}.body`)}</p>
            <Link to="/login" className="mt-6 flex h-12 items-center justify-center rounded-2xl border-2 border-sc-teal font-semibold text-sc-teal" data-testid="welcome-go-login">{t('welcome.goLogin')}</Link>
          </div>
        )}

        {info?.state === 'ok' && (
          <>
            <h1 className="mt-6 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl" data-testid="welcome-title">{t('reset.pageTitle', { name: info.name ?? '' })}</h1>
            <p className="mt-2 text-slate-600">{t('reset.pageSubtitle')}</p>
            <form onSubmit={onSubmit} className="mt-8 space-y-5" noValidate>
              <label className="block">
                <span className="mb-1.5 block text-sm font-semibold text-slate-700">{t('login.email')}</span>
                <div className="relative">
                  <input type="email" value={info.email ?? ''} readOnly aria-readonly="true" tabIndex={-1} autoComplete="username" className="h-12 w-full cursor-default rounded-xl border border-sc-line bg-slate-100 px-4 pr-11 text-base text-slate-600 focus:outline-none" data-testid="welcome-email" />
                  <Lock className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden />
                </div>
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-semibold text-slate-700">{t('welcome.newPassword')}</span>
                <input type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} className={inputCls} data-testid="welcome-password" />
                <span className="mt-1 block text-xs text-slate-500">{t('welcome.rule')}</span>
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-semibold text-slate-700">{t('welcome.repeat')}</span>
                <input type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} className={inputCls} data-testid="welcome-password-repeat" />
              </label>
              {error && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700" role="alert" data-testid="welcome-error">{error}</p>}
              <button type="submit" disabled={busy} data-testid="welcome-submit" className="flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-sc-teal text-base font-semibold text-white hover:bg-sc-teal-hover disabled:opacity-70">
                {busy && <Loader2 className="h-5 w-5 animate-spin" />}{t('reset.pageSubmit')}
              </button>
            </form>
          </>
        )}
      </div>
      <div className="flex justify-center"><PoweredBy /></div>
    </div>
  );
}
